// ─────────────────────────────────────────────────────────────
// CloudflareHandler — implements CloudHandler interface
// Talks to st-daw-open-save-sync at
// api.sundaytriggers.com/daw/projects/*
// which proxies all operations to the st-daw-projects R2 bucket.
// ─────────────────────────────────────────────────────────────

import {CloudHandler} from "@opendaw/studio-core"
import {CloudflareConfig} from "./CloudflareConfig"
import {ClerkAuth} from "../auth/ClerkAuth"

export class CloudflareHandler implements CloudHandler {
    readonly #baseUrl: string

    constructor() {
        this.#baseUrl = CloudflareConfig.WORKER_URL.replace(/\/$/, "")
    }

    // ── Auth headers — async, uses Clerk JWT ──────────────────

    async #headers(): Promise<HeadersInit> {
        const token = await ClerkAuth.getToken()
        return {"Authorization": `Bearer ${token}`}
    }

    // ── CloudHandler interface ────────────────────────────────

    async alive(): Promise<void> {
        const res = await fetch(`${this.#baseUrl}/alive`, {
            headers: await this.#headers()
        })
        if (!res.ok) throw new Error(`Cloudflare DAW worker unreachable: ${res.status}`)
    }

    async upload(path: string, data: ArrayBuffer): Promise<void> {
        const url = `${this.#baseUrl}/file?path=${encodeURIComponent(path)}`
        const res = await fetch(url, {
            method: "PUT",
            headers: {
                ...await this.#headers(),
                "Content-Type": "application/octet-stream"
            },
            body: data
        })
        if (!res.ok) {
            const text = await res.text()
            throw new Error(`Cloudflare upload failed [${path}]: ${res.status} ${text}`)
        }
    }

    async download(path: string): Promise<ArrayBuffer> {
        const url = `${this.#baseUrl}/file?path=${encodeURIComponent(path)}`
        const res = await fetch(url, {headers: await this.#headers()})
        if (res.status === 404) {
            // Match the same error type as Google/Dropbox handlers
            const {Errors} = await import("@opendaw/lib-std")
            throw new Errors.FileNotFound(path)
        }
        if (!res.ok) {
            const text = await res.text()
            throw new Error(`Cloudflare download failed [${path}]: ${res.status} ${text}`)
        }
        return res.arrayBuffer()
    }

    async exists(path: string): Promise<boolean> {
        const url = `${this.#baseUrl}/file?path=${encodeURIComponent(path)}`
        const res = await fetch(url, {
            method: "HEAD",
            headers: await this.#headers()
        })
        return res.ok
    }

    async list(path?: string): Promise<string[]> {
        const prefix = path ?? ""
        const url = `${this.#baseUrl}/list?path=${encodeURIComponent(prefix)}`
        const res = await fetch(url, {headers: await this.#headers()})
        if (!res.ok) {
            const text = await res.text()
            throw new Error(`Cloudflare list failed [${prefix}]: ${res.status} ${text}`)
        }
        return res.json() as Promise<string[]>
    }

    async delete(path: string): Promise<void> {
        const url = `${this.#baseUrl}/file?path=${encodeURIComponent(path)}`
        const res = await fetch(url, {
            method: "DELETE",
            headers: await this.#headers()
        })
        if (!res.ok && res.status !== 404) {
            const text = await res.text()
            throw new Error(`Cloudflare delete failed [${path}]: ${res.status} ${text}`)
        }
    }
}
